import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SliceProps } from '../../store';
import {
    urlDocumentAdditionalNoticeCreate,
    urlDocumentAdditionalNoticeTypes,
} from '../../../Constants';
import dayjs, { Dayjs } from 'dayjs';
import { IProjectUserContact } from '../sliceProjectUserList';
import { PickerChangeHandlerContext, DateValidationError } from '@mui/x-date-pickers';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsCreateNoticeState = {
    isDialogOpen: false,
    isNoticeCreated: false,
    loading: false,
    loadingNoticeTypes: false,
    loadingUsers: false,
    error: null,
    noticeTypes: null,
    users: [],
};

const reducers = {
    openCreateNoticeDialog(state: IProjectDocumentsCreateNoticeState) {
        state.isDialogOpen = true;
    },

    closeCreateNoticeDialog(state: IProjectDocumentsCreateNoticeState) {
        state.isDialogOpen = false;
        state.isNoticeCreated = false;
    },

    resetCreateNoticeErrorState(state: IProjectDocumentsCreateNoticeState) {
        state.error = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get notice types for Create Notice Dialog
     */
    getNoticeTypes: async (config: { replicaId: string }): Promise<IGetNoticeTypeResponse> => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };
            const response = await apiFetch(urlDocumentAdditionalNoticeTypes, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmCreateNotice: async (
        config: {
            replicaId: string;
            taskType: string;
            dueDate: Dayjs;
            comment: string;
            assignees: IProjectUserContact[];
            approvalMethod: NoticeApprovalMethodEnum;
            dueDateErrorContext: PickerChangeHandlerContext<DateValidationError>;
        },
        thunkApi,
    ): Promise<unknown> => {
        try {
            const {
                replicaId,
                taskType,
                dueDate,
                comment,
                assignees,
                approvalMethod,
                dueDateErrorContext,
            } = config;
            const { data: documents } = thunkApi.getState().projectDocuments.info;

            const taskDays =
                dueDate?.isValid() && !dueDateErrorContext?.validationError
                    ? dueDate.startOf('day').diff(dayjs().startOf('day'), 'days').toString()
                    : '';

            const userList = assignees.map(item => item.notesName);

            const formData = new FormData();
            formData.append('universalIds', documents.map(item => item.universalId).join('|'));

            const noticeData = {
                redliningColor: '#8a2be2',
                taskDays: taskDays,
                taskType: taskType,
                comment: comment,
                userDistribution: approvalMethod.toString(),
                userList: userList,
            };

            formData.append('noticeData', JSON.stringify(noticeData));

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlDocumentAdditionalNoticeCreate, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsCreateNoticeState>) => {
        /**
         * Reducers for the 'getNoticeTypes' thunk.
         */
        builder.addCase(
            getNoticeTypes.pending,
            (state: Draft<IProjectDocumentsCreateNoticeState>) => {
                state.loadingNoticeTypes = true;
                state.error = null;
            },
        );

        builder.addCase(
            getNoticeTypes.fulfilled,
            (
                state: Draft<IProjectDocumentsCreateNoticeState>,
                action: IAction<IGetNoticeTypeResponse>,
            ) => {
                state.loadingNoticeTypes = false;
                state.error = null;

                state.noticeTypes = action.payload.TaskTypes;
            },
        );

        builder.addCase(
            getNoticeTypes.rejected,
            (
                state: Draft<IProjectDocumentsCreateNoticeState>,
                action: IAction<SerializedError>,
            ) => {
                state.loadingNoticeTypes = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onConfirmCreateNotice' thunk.
         */
        builder.addCase(
            onConfirmCreateNotice.pending,
            (state: Draft<IProjectDocumentsCreateNoticeState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmCreateNotice.fulfilled,
            (state: Draft<IProjectDocumentsCreateNoticeState>) => {
                state.isNoticeCreated = true;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmCreateNotice.rejected,
            (
                state: Draft<IProjectDocumentsCreateNoticeState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const onConfirmCreateNotice = createAsyncThunk(
    urlDocumentAdditionalNoticeCreate,
    thunks.onConfirmCreateNotice,
);

export const getNoticeTypes = createAsyncThunk(
    urlDocumentAdditionalNoticeTypes,
    thunks.getNoticeTypes,
);

export enum NoticeApprovalMethodEnum {
    AtLeastOneNeedToApprove = '0',
    AllNeedToApprove = '1',
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentsCreateNoticeState extends SliceProps {
    isDialogOpen: boolean;
    loadingUsers: boolean;
    loadingNoticeTypes: boolean;
    isNoticeCreated: boolean;
    users: IProjectUserContact[];
    noticeTypes: Record<string, string>[];
}

interface IGetNoticeTypeResponse {
    TaskTypes: Record<string, string>[];
}

/**
 * Create the redux slice.
 */
export const projectDocumentsCreateNoticeSlice = createSlice({
    name: 'projectDocumentsCreateNotice',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openCreateNoticeDialog, closeCreateNoticeDialog, resetCreateNoticeErrorState } =
    projectDocumentsCreateNoticeSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsCreateNoticeSlice.reducer;
